import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useIsMobile, merge } from "../hooks/useIsMobile";
import { useJudgeTiming } from "../hooks/useJudgeTiming";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const API = process.env.REACT_APP_API_URL || "";

const COLORS = {
  bg: "#F4F4F0",
  bgCard: "#FFFFFF",
  ink: "#111214",
  inkSoft: "#3A3C40",
  inkMuted: "#7A7C82",
  blue: "#2D7FF9",
  blueDeep: "#1A5EC4",
  mint: "#1AC98E",
  mintBg: "#E6FAF4",
  coral: "#FF4D2E",
  coralBg: "#FFF0ED",
  amber: "#E8A020",
  amberBg: "#FEF6E7",
  border: "rgba(17,18,20,0.09)",
  borderMd: "rgba(17,18,20,0.14)",
};

const DISPLAY = "'Plus Jakarta Sans', sans-serif";
const BODY = "'DM Sans', sans-serif";
const MONO = "'DM Mono', monospace";

export default function Communities() {
  const isMobile = useIsMobile();
  const judgeTiming = useJudgeTiming();
  const [formState, setFormState] = useState("idle");
  const [formData, setFormData] = useState({
    first_name: "", last_name: "", email: "", site_url: "", comments: "",
  });

  const handleChange = (e) =>
    setFormData((d) => ({ ...d, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormState("sending");
    try {
      const res = await fetch(`${API}/api/community-pilot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error();
      setFormState("sent");
    } catch {
      setFormState("error");
    }
  };

  return (
    <div style={s.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');
        @keyframes rise {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .c-reveal { animation: rise 0.85s ease forwards; opacity: 0; }
        .c-d1 { animation-delay: 0.05s; }
        .c-d2 { animation-delay: 0.15s; }
        .c-d3 { animation-delay: 0.25s; }
        .c-d4 { animation-delay: 0.38s; }
        @media (prefers-reduced-motion: reduce) {
          .c-reveal { animation: none; opacity: 1; }
        }
      `}</style>

      {/* ── Nav ── */}
      <SiteHeader />

      {/* ── Hero ── */}
      <section style={merge(s.hero, s.heroMobile, isMobile)}>
        <div style={s.container}>
          <div style={merge(s.heroGrid, s.heroGridMobile, isMobile)}>
            <div>
              <span className="c-reveal c-d1" style={{ ...s.eyebrow, color: COLORS.blueDeep }}>
                disputefai for community owners &amp; moderators
              </span>
              <h1 className="c-reveal c-d2" style={merge(s.display, s.displayMobile, isMobile)}>
                Running a community is rewarding.<br />
                <em style={{ fontStyle: "normal", color: COLORS.amber }}>Until the conflict starts.</em>
              </h1>
              <p className="c-reveal c-d3" style={s.heroLede}>
                Member disputes are draining, time-consuming, and nearly impossible to resolve
                without someone feeling the mod sided against them. disputefai gives your members
                a neutral place to present their case — so you stop being the referee and start
                being the community builder again.
              </p>
              <div className="c-reveal c-d4" style={s.heroActions}>
                {/* <a href="#apply" style={s.btnPrimary}>Apply for free pilot</a> */}
                <a href="#how" style={s.btnOutline}>See how it works</a>
              </div>
            </div>

            {/* Thread mock */}
            <ThreadMock />
          </div>
        </div>
      </section>

      {/* ── The Problem ── */}
      <section style={s.problem}>
        <div style={s.container}>
          <span style={{ ...s.eyebrow, color: COLORS.amber, marginBottom: 20 }}>
            Why community conflict is so hard to manage
          </span>
          <h2 style={s.problemH2}>Every active community eventually faces this.</h2>
          <div style={merge(s.problemGrid, s.problemGridMobile, isMobile)}>
            <ProblemCard icon="😤" title="Someone always thinks you're biased"
              text="When a moderator steps in, one side almost always feels the call went against them personally. Even a fair decision can damage trust when it comes from inside the community." />
            <ProblemCard icon="🔥" title="Unresolved conflict escalates fast"
              text="A dispute between two members rarely stays between two members. Others take sides, threads get reported, good people leave. What started as one argument becomes a community-wide fracture." />
            <ProblemCard icon="⏱️" title="Moderating it takes hours you don't have"
              text="Reading back through threads, understanding context, making a call, then fielding the blowback. This is unpaid work that burns out the best moderators and distracts from everything else." />
          </div>
        </div>
      </section>

      {/* ── How It Helps ── */}
      <section style={s.helps}>
        <div style={s.container}>
          <span style={{ ...s.eyebrow, color: COLORS.blueDeep }}>How disputefai helps</span>
          <div style={merge(s.helpsGrid, s.helpsGridMobile, isMobile)}>
            <div>
              <h2 style={merge(s.helpsH2, s.helpsH2Mobile, isMobile)}>
                A neutral third party that takes the burden off your team.
              </h2>
              <p style={s.helpsSub}>
                disputefai doesn't replace moderation. It gives moderators a structured, credible
                tool to use when a dispute needs more than a mod's opinion — and both parties
                deserve a proper hearing.
              </p>
            </div>
            <div>
              {[
                { num: "01", title: "Genuinely neutral", text: "disputefai has no history with your members, no community relationships, and no stake in who's right. Its assessment is based entirely on what each side presents — nothing else." },
                { num: "02", title: "Gives both sides a proper hearing", text: "Each member submits their version of events and their evidence. The process is the same for both sides. Neither party can claim the other got special treatment." },
                { num: "03", title: "Protects moderators from blowback", text: 'Instead of "the mod sided with their friend," you get "here\'s the independent written statement and the reasoning." Moderators can enforce it without putting themselves in the middle.' },
                { num: "04", title: "Documented and transparent", text: "Every case produces a written statement. Both parties know what was considered and why. Communities that handle conflict transparently retain the trust of their wider membership." },
              ].map((p, i) => (
                <div key={i} style={{ ...s.helpsPoint, ...(i === 3 ? { borderBottom: `1px solid ${COLORS.border}` } : {}) }}>
                  <div style={s.helpsPointNum}>{p.num}</div>
                  <div>
                    <h5 style={s.helpsPointTitle}>{p.title}</h5>
                    <p style={s.helpsPointText}>{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how" style={s.howSection}>
        <div style={s.container}>
          <span style={{ ...s.eyebrow, color: COLORS.blueDeep }}>The process</span>
          <h2 style={merge(s.howH2, s.howH2Mobile, isMobile)}>How it works for your community.</h2>
          <p style={s.howSub}>
            The same evidence-based process used by individual users, applied to your community's
            disputes. As a moderator, your role is simply to refer the case — disputefai handles
            the rest.
          </p>
          <div style={merge(s.stepsGrid, s.stepsGridMobile, isMobile)}>
            {[
              { num: "01", title: "Mod refers the dispute", text: "When a conflict escalates beyond what a standard moderation call can handle, the moderator refers both members to disputefai with a link and a brief description of the issue." },
              { num: "02", title: "Both sides submit their case", text: `Each member submits their account in plain language. They can include screenshots, links, post history, timestamps — any evidence they have. ${judgeTiming.label} to respond.` },
              { num: "03", title: "Independent AI review", text: "The AI review panel reads both sides and produces a written statement: what each party argued, what the evidence showed, and which position was better supported." },
              { num: "04", title: "Statement delivered to all", text: "Both members and the referring moderator receive the same written statement. The moderator can act on it with confidence. The community can see the process was fair." },
            ].map((step, i) => (
              <div key={i} style={s.stepCard}>
                <div style={s.stepNum}>{step.num}</div>
                <h4 style={s.stepTitle}>{step.title}</h4>
                <p style={s.stepText}>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pilot Offer + Form (temporarily disabled) ── */}
      {false && (
      <section id="apply" style={s.pilotSection}>
        <div style={s.pilotBg} />
        <div style={{ ...s.container, position: "relative" }}>
          <div style={merge(s.pilotInner, s.pilotInnerMobile, isMobile)}>
            <div>
              <span style={{ ...s.eyebrow, color: COLORS.mint }}>Free pilot programme</span>
              <h2 style={merge(s.pilotH2, s.pilotH2Mobile, isMobile)}>Try it for free. Work directly with us.</h2>
              <p style={s.pilotSub}>
                We're working with a small number of community owners and moderators to pilot
                the community version of disputefai. Pilot partners get direct access, free use
                during the pilot, and input into how the product develops.
              </p>

              <div style={s.pilotSteps}>
                {[
                  { num: "1", title: "Try the consumer version first", text: <>The best way to understand how disputefai works is to use it. File a case yourself at <Link to="/home" style={{ color: COLORS.blue, fontWeight: 500 }}>disputefai.com</Link> — it's free for the single-reviewer tier. Get a feel for the process before committing your community.</> },
                  { num: "2", title: "Apply for the pilot", text: "Fill in the form on this page. Tell us about your community — what platform it's on, how many members, what kinds of conflicts you deal with. We'll review every application personally." },
                  { num: "3", title: "Work directly with the team", text: "Accepted pilot partners get a direct line to us. We'll set you up, handle your first cases alongside you, and iterate based on what we learn together." },
                ].map((ps, i) => (
                  <div key={i} style={s.pilotStep}>
                    <div style={s.pilotStepNum}>{ps.num}</div>
                    <div>
                      <h5 style={s.pilotStepTitle}>{ps.title}</h5>
                      <p style={s.pilotStepText}>{typeof ps.text === "string" ? ps.text : ps.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div style={s.pilotNote}>
                <strong style={{ color: COLORS.ink, fontWeight: 600 }}>No cost during the pilot.</strong>{" "}
                Pilot partners use disputefai free of charge for the duration of the programme.
                In exchange, we ask for honest feedback and a willingness to share what's working
                and what isn't.
              </div>
            </div>

            {/* Form */}
            <div style={s.formCard} id="form">
              <h3 style={s.formTitle}>Apply for the pilot</h3>
              <p style={s.formSub}>Tell us about yourself and your community. We'll be in touch within a few days.</p>

              {formState === "sent" ? (
                <div style={{ padding: 24, textAlign: "center", color: COLORS.mint, fontWeight: 600, fontSize: 16 }}>
                  Thanks! We'll be in touch soon.
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div style={merge(s.formRow, s.formRowMobile, isMobile)}>
                    <div style={s.formGroup}>
                      <label style={s.formLabel} htmlFor="fname">First name</label>
                      <input style={s.formInput} type="text" id="fname" name="first_name"
                        placeholder="Alex" required value={formData.first_name} onChange={handleChange} />
                    </div>
                    <div style={s.formGroup}>
                      <label style={s.formLabel} htmlFor="lname">Last name</label>
                      <input style={s.formInput} type="text" id="lname" name="last_name"
                        placeholder="Rivera" required value={formData.last_name} onChange={handleChange} />
                    </div>
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.formLabel} htmlFor="email">Email address</label>
                    <input style={s.formInput} type="email" id="email" name="email"
                      placeholder="alex@example.com" required value={formData.email} onChange={handleChange} />
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.formLabel} htmlFor="site">Community or site address</label>
                    <input style={s.formInput} type="url" id="site" name="site_url"
                      placeholder="https://yourforum.com" value={formData.site_url} onChange={handleChange} />
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.formLabel} htmlFor="comments">Tell us about your community</label>
                    <textarea style={s.formTextarea} id="comments" name="comments"
                      placeholder="What platform is it on? How many members? What kinds of conflicts come up most often? Anything else you'd like us to know."
                      value={formData.comments} onChange={handleChange} />
                  </div>
                  <button type="submit" style={s.formSubmit} disabled={formState === "sending"}>
                    {formState === "sending" ? "Sending…" : "Send application →"}
                  </button>
                  {formState === "error" && (
                    <p style={{ color: COLORS.coral, fontSize: 13, textAlign: "center", marginTop: 10 }}>
                      Something went wrong. Please try again.
                    </p>
                  )}
                  <p style={s.formFine}>We read every application personally and respond to all of them. No automated replies.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── Footer ── */}
      <SiteFooter />
    </div>
  );
}

// ── Thread Mock ──────────────────────────────────────────────────────────────
function ThreadMock() {
  const judgeTiming = useJudgeTiming();
  return (
    <div className="c-reveal c-d3" style={s.threadMock}>
      <div style={s.threadTopbar}>
        <div style={{ display: "flex", gap: 6 }}>
          <span style={s.threadDot} /><span style={s.threadDot} /><span style={s.threadDot} />
        </div>
        <div style={s.threadTopbarTitle}>r/webdev · flagged thread</div>
      </div>
      <div style={s.threadHeader}>
        <span style={s.threadFlagDot} />
        <span style={s.threadFlag}>3 reports · Escalating</span>
        <span style={{ marginLeft: 8, color: COLORS.inkMuted, fontSize: 13, fontWeight: 600 }}>
          Who actually created the design system for Project Delta?
        </span>
      </div>
      <div style={{ padding: "4px 0 0" }}>
        <ThreadPost avatar="KL" color="#5B6AF0" user="kl_design" time="3h ago"
          text="Just want to set the record straight — I built the entire component library for this project. Starting to see my work attributed to someone else in this thread and that's not okay." />
        <ThreadPost avatar="MR" color="#E05C3A" user="m_reyes_dev" time="2h ago" heated
          text="That's a serious mischaracterisation. I have commit history going back to January. Happy to share it publicly. Not sure what you're trying to pull here." />
        <div style={{ ...s.threadPost, background: "rgba(232,160,32,0.03)" }}>
          <div style={s.threadPostHeader}>
            <div style={{ ...s.threadAvatar, background: COLORS.blue }}>🛡</div>
            <span style={s.threadUsername}>
              <span style={{ color: COLORS.amber }}>mod_sarah</span>
            </span>
            <span style={s.threadTime}>1h ago</span>
            <span style={s.modBadge}>MOD</span>
          </div>
          <div style={s.threadText}>
            This thread is getting heated and I want both sides to feel heard. I'm referring
            this to an independent review — it'll give both of you a proper hearing without me
            having to be the one to decide.
          </div>
        </div>
        <div style={s.modBanner}>
          <span style={{ fontSize: 15, flexShrink: 0 }}>📋</span>
          <div style={{ fontSize: 12.5, color: COLORS.ink, lineHeight: 1.5 }}>
            <strong style={{ fontWeight: 600 }}>Referred to disputefai.</strong> Both members
            have been invited to submit their evidence and receive an independent written
            statement from the AI review panel.
          </div>
        </div>
        <div style={s.disputefaiBanner}>
          <div style={s.disputefaiLogo}>
            disputef<span style={s.disputefaiLogoBadge}>.ai</span>
          </div>
          <div style={{ fontSize: 12, color: COLORS.inkSoft, lineHeight: 1.45 }}>
            Independent review in progress. Both sides have {judgeTiming.label} to respond.
          </div>
          <a href="#apply" style={s.disputefaiLink}>View case →</a>
        </div>
      </div>
    </div>
  );
}

function ThreadPost({ avatar, color, user, time, text, heated }) {
  return (
    <div style={s.threadPost}>
      <div style={s.threadPostHeader}>
        <div style={{ ...s.threadAvatar, background: color }}>{avatar}</div>
        <span style={s.threadUsername}>{user}</span>
        <span style={s.threadTime}>{time}</span>
      </div>
      <div style={{ ...s.threadText, ...(heated ? { color: COLORS.ink, fontWeight: 500 } : {}) }}>
        {text}
      </div>
    </div>
  );
}

function ProblemCard({ icon, title, text }) {
  return (
    <div style={s.problemCard}>
      <div style={{ fontSize: 26, marginBottom: 18 }}>{icon}</div>
      <h4 style={s.problemCardTitle}>{title}</h4>
      <p style={s.problemCardText}>{text}</p>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const s = {
  // Flex column so SiteFooter's `marginTop: auto` pins it to the bottom of the
  // viewport on short pages — same shell as pageStyles.root.
  page: { background: COLORS.bg, color: COLORS.ink, fontFamily: BODY, fontSize: 15, lineHeight: 1.55, minHeight: "100vh", WebkitFontSmoothing: "antialiased", overflowX: "hidden", display: "flex", flexDirection: "column" },
  container: { maxWidth: 1180, margin: "0 auto", padding: "0 36px" },
  eyebrow: { fontFamily: MONO, fontSize: 11.5, fontWeight: 500, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 20, display: "block" },

  // ── Hero ──
  hero: { padding: "88px 0 96px" },
  heroMobile: { padding: "56px 0 64px" },
  heroGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" },
  heroGridMobile: { gridTemplateColumns: "1fr", gap: 52 },
  display: { fontFamily: DISPLAY, fontWeight: 800, fontSize: "clamp(36px, 4.8vw, 58px)", lineHeight: 1.05, letterSpacing: "-0.03em", color: COLORS.ink, marginBottom: 22, marginTop: 0 },
  displayMobile: { fontSize: "clamp(32px, 7vw, 42px)" },
  heroLede: { fontSize: 17.5, color: COLORS.inkSoft, lineHeight: 1.65, marginBottom: 32, maxWidth: "50ch" },
  heroActions: { display: "flex", gap: 12, flexWrap: "wrap" },
  btnPrimary: { display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 24px", borderRadius: 9, fontFamily: BODY, fontSize: 15, fontWeight: 600, textDecoration: "none", cursor: "pointer", transition: "all 0.18s", border: "none", lineHeight: 1, background: COLORS.ink, color: "#fff" },
  btnOutline: { display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 24px", borderRadius: 9, fontFamily: BODY, fontSize: 15, fontWeight: 600, textDecoration: "none", cursor: "pointer", transition: "all 0.18s", lineHeight: 1, background: "transparent", color: COLORS.ink, border: `1.5px solid ${COLORS.borderMd}` },

  // ── Thread Mock ──
  threadMock: { background: COLORS.bgCard, borderRadius: 14, border: `1px solid ${COLORS.border}`, overflow: "hidden", boxShadow: "0 4px 32px -8px rgba(17,18,20,0.14)" },
  threadTopbar: { background: "#1E1F23", padding: "11px 16px", display: "flex", alignItems: "center", gap: 10 },
  threadDot: { width: 11, height: 11, borderRadius: "50%", background: "rgba(255,255,255,0.15)", display: "inline-block" },
  threadTopbarTitle: { fontFamily: MONO, fontSize: 11, color: "rgba(255,255,255,0.45)", flex: 1, textAlign: "center" },
  threadHeader: { padding: "14px 16px", borderBottom: `1px solid ${COLORS.border}`, background: "rgba(232,160,32,0.05)", display: "flex", alignItems: "center", gap: 10 },
  threadFlagDot: { width: 7, height: 7, borderRadius: "50%", background: COLORS.amber, flexShrink: 0 },
  threadFlag: { fontSize: 13, fontWeight: 600, color: COLORS.amber, display: "flex", alignItems: "center", gap: 6 },
  threadPost: { padding: "14px 16px", borderBottom: `1px solid ${COLORS.border}` },
  threadPostHeader: { display: "flex", alignItems: "center", gap: 8, marginBottom: 7 },
  threadAvatar: { width: 26, height: 26, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: "#fff" },
  threadUsername: { fontSize: 12.5, fontWeight: 600, color: COLORS.ink },
  threadTime: { fontSize: 11, color: COLORS.inkMuted, fontFamily: MONO },
  threadText: { fontSize: 13.5, color: COLORS.inkSoft, lineHeight: 1.55 },
  modBadge: { fontSize: 11, background: COLORS.amberBg, color: COLORS.amber, padding: "2px 8px", borderRadius: 100, fontWeight: 600, marginLeft: 4 },
  modBanner: { margin: "10px 16px 0", background: COLORS.amberBg, border: "1px solid rgba(232,160,32,0.3)", borderRadius: 8, padding: "11px 14px", display: "flex", gap: 10, alignItems: "flex-start" },
  disputefaiBanner: { margin: "10px 16px 16px", background: "linear-gradient(135deg, rgba(45,127,249,0.06), rgba(26,201,142,0.06))", border: "1px solid rgba(45,127,249,0.18)", borderRadius: 8, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 },
  disputefaiLogo: { fontFamily: BODY, fontWeight: 600, fontSize: 13, color: COLORS.ink, whiteSpace: "nowrap" },
  disputefaiLogoBadge: { background: COLORS.blue, color: "#fff", fontSize: 9, fontWeight: 600, padding: "1px 5px", borderRadius: 4, marginLeft: 2 },
  disputefaiLink: { fontSize: 12, fontWeight: 600, color: COLORS.blue, textDecoration: "none", whiteSpace: "nowrap", marginLeft: "auto", flexShrink: 0 },

  // ── Problem ──
  problem: { padding: "110px 0", background: COLORS.ink, color: "#fff" },
  problemH2: { fontFamily: DISPLAY, fontWeight: 800, fontSize: "clamp(32px, 4vw, 50px)", letterSpacing: "-0.025em", lineHeight: 1.08, marginBottom: 56, maxWidth: "20ch", marginTop: 0 },
  problemGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 },
  problemGridMobile: { gridTemplateColumns: "1fr", gap: 16 },
  problemCard: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "30px 26px" },
  problemCardTitle: { fontFamily: DISPLAY, fontSize: 20, fontWeight: 700, marginBottom: 10, letterSpacing: "-0.01em", marginTop: 0 },
  problemCardText: { fontSize: 14.5, color: "rgba(255,255,255,0.7)", lineHeight: 1.65, margin: 0 },

  // ── Helps ──
  helps: { padding: "110px 0" },
  helpsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 72, alignItems: "center", marginTop: 56 },
  helpsGridMobile: { gridTemplateColumns: "1fr", gap: 48 },
  helpsH2: { fontFamily: DISPLAY, fontWeight: 800, fontSize: "clamp(32px, 4vw, 48px)", letterSpacing: "-0.025em", lineHeight: 1.08, marginBottom: 18, marginTop: 0 },
  helpsH2Mobile: { fontSize: "clamp(28px, 6vw, 38px)" },
  helpsSub: { fontSize: 17, color: COLORS.inkSoft, lineHeight: 1.65, marginBottom: 0 },
  helpsPoint: { display: "flex", gap: 20, padding: "22px 0", borderTop: `1px solid ${COLORS.border}` },
  helpsPointNum: { fontFamily: MONO, fontSize: 11, color: COLORS.blue, letterSpacing: "0.08em", paddingTop: 4, flexShrink: 0, width: 28 },
  helpsPointTitle: { fontFamily: DISPLAY, fontSize: 18, fontWeight: 700, marginBottom: 6, letterSpacing: "-0.01em", marginTop: 0 },
  helpsPointText: { fontSize: 14.5, color: COLORS.inkSoft, lineHeight: 1.6, margin: 0 },

  // ── How It Works ──
  howSection: { padding: "110px 0", background: COLORS.bgCard, borderTop: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}` },
  howH2: { fontFamily: DISPLAY, fontWeight: 800, fontSize: "clamp(32px, 4vw, 48px)", letterSpacing: "-0.025em", lineHeight: 1.08, marginBottom: 16, marginTop: 0 },
  howH2Mobile: { fontSize: "clamp(28px, 6vw, 38px)" },
  howSub: { fontSize: 17, color: COLORS.inkSoft, lineHeight: 1.6, maxWidth: "58ch", marginBottom: 56 },
  stepsGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, background: COLORS.border, borderRadius: 14, overflow: "hidden" },
  stepsGridMobile: { gridTemplateColumns: "1fr" },
  stepCard: { background: COLORS.bgCard, padding: "30px 24px" },
  stepNum: { fontFamily: DISPLAY, fontSize: 42, fontWeight: 800, color: "rgba(17,18,20,0.06)", lineHeight: 1, marginBottom: 16 },
  stepTitle: { fontFamily: DISPLAY, fontSize: 18, fontWeight: 700, marginBottom: 10, letterSpacing: "-0.01em", color: COLORS.ink, marginTop: 0 },
  stepText: { fontSize: 14, color: COLORS.inkSoft, lineHeight: 1.6, margin: 0 },

  // ── Pilot ──
  pilotSection: { padding: "110px 0", position: "relative", overflow: "hidden" },
  pilotBg: { position: "absolute", inset: 0, background: "radial-gradient(ellipse 60% 50% at 70% 50%, rgba(26,201,142,0.07), transparent)", pointerEvents: "none" },
  pilotInner: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "start" },
  pilotInnerMobile: { gridTemplateColumns: "1fr", gap: 52 },
  pilotH2: { fontFamily: DISPLAY, fontWeight: 800, fontSize: "clamp(32px, 4vw, 50px)", letterSpacing: "-0.025em", lineHeight: 1.08, marginBottom: 20, marginTop: 0 },
  pilotH2Mobile: { fontSize: "clamp(28px, 6vw, 38px)" },
  pilotSub: { fontSize: 17, color: COLORS.inkSoft, lineHeight: 1.65, marginBottom: 28 },
  pilotSteps: { display: "flex", flexDirection: "column", gap: 14, marginBottom: 36 },
  pilotStep: { display: "flex", gap: 14, alignItems: "flex-start", padding: "16px 18px", background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 10 },
  pilotStepNum: { width: 26, height: 26, borderRadius: "50%", background: COLORS.ink, color: "#fff", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 },
  pilotStepTitle: { fontFamily: DISPLAY, fontSize: 15, fontWeight: 700, marginBottom: 3, marginTop: 0 },
  pilotStepText: { fontSize: 13.5, color: COLORS.inkSoft, lineHeight: 1.5, margin: 0 },
  pilotNote: { fontSize: 14, color: COLORS.inkMuted, lineHeight: 1.6, padding: "16px 18px", background: COLORS.mintBg, borderRadius: 10, border: "1px solid rgba(26,201,142,0.2)" },

  // ── Form ──
  formCard: { background: COLORS.bgCard, borderRadius: 16, border: `1px solid ${COLORS.border}`, padding: "40px 36px", boxShadow: "0 4px 32px -8px rgba(17,18,20,0.1)" },
  formTitle: { fontFamily: DISPLAY, fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 6, marginTop: 0 },
  formSub: { fontSize: 14.5, color: COLORS.inkMuted, marginBottom: 28, lineHeight: 1.5 },
  formRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },
  formRowMobile: { gridTemplateColumns: "1fr" },
  formGroup: { marginBottom: 16 },
  formLabel: { display: "block", fontSize: 13, fontWeight: 600, color: COLORS.ink, marginBottom: 7, letterSpacing: "0.01em" },
  formInput: { width: "100%", padding: "11px 14px", border: `1.5px solid ${COLORS.borderMd}`, borderRadius: 8, fontFamily: BODY, fontSize: 14.5, color: COLORS.ink, background: COLORS.bg, outline: "none", boxSizing: "border-box" },
  formTextarea: { width: "100%", padding: "11px 14px", border: `1.5px solid ${COLORS.borderMd}`, borderRadius: 8, fontFamily: BODY, fontSize: 14.5, color: COLORS.ink, background: COLORS.bg, outline: "none", resize: "vertical", minHeight: 110, lineHeight: 1.55, boxSizing: "border-box" },
  formSubmit: { width: "100%", padding: "14px 24px", background: COLORS.ink, color: "#fff", border: "none", borderRadius: 9, fontFamily: BODY, fontSize: 15, fontWeight: 600, cursor: "pointer", transition: "all 0.18s", marginTop: 4 },
  formFine: { fontSize: 12, color: COLORS.inkMuted, textAlign: "center", marginTop: 14, lineHeight: 1.55 },
};
